import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { FaRegTrashAlt } from "react-icons/fa";
import { FiFileText } from "react-icons/fi";
import CustomSelect from "../CustomSelect";
import usePageWidth from "../shared/usePageWidth";
import styles from './styles.module.css';

const TYPE_OPTIONS = [
  { value: 'disciplines', label: 'Дисциплины' },
  { value: 'practices', label: 'Практики' },
  { value: 'modules', label: 'Модули' },
];

// подпись третьего поля отбора идёт за видом данных
const DISCIPLINE_FILTER_LABEL = {
  disciplines: 'Дисциплина РПД',
  practices: 'Практика',
  modules: 'Модуль',
};

// «Все» в CustomSelect — пустая строка, режим хранит отсутствие отбора нулём
const ALL = '';
const toFilter = value => (value === ALL || value == null ? 0 : value);

// многоточие ставится только там, где пропущено больше одной страницы
const pagerItems = (page, pages) => {
  if (pages <= 1) return [];
  const keep = new Set([1, pages, page - 1, page, page + 1]);
  if (page <= 4) [2, 3, 4, 5].forEach(i => keep.add(i));
  if (page >= pages - 3) [pages - 1, pages - 2, pages - 3, pages - 4].forEach(i => keep.add(i));
  const list = [...keep].filter(i => i >= 1 && i <= pages).sort((a, b) => a - b);

  const items = [];
  list.forEach((value, i) => {
    if (i) {
      const gap = value - list[i - 1];
      if (gap === 2) items.push(list[i - 1] + 1);
      else if (gap > 2) items.push('gap');
    }
    items.push(value);
  });
  return items;
};

const EducationWorkPrograms = ({
  fetchEducationPrograms,
  fetchCurriculum,
  fetchDisciplines,
  fetchYears,
  fetchEdy,
  fetchCourse,
  fetchMetaCourse,
  discipline,
  disciplines,
  disciplineRPD,
  educationYears,
  curriculum,
  schoolYears,
  pagesCount,
  totalCount,
  educationPrograms,
  ...props
}) => {
  usePageWidth('pageWide');

  const { isLoading } = props;
  const [curriculumId, setCurriculumId] = useState(0)
  const [disciplineId, setDisciplineId] = useState(0)
  const [disciplineRPDId, setDisciplineRPDId] = useState(0)
  const [eyId, setEyId] = useState(0)
  const [syid, setSyid] = useState(0)
  const [schoolYearId, setSchoolYearId] = useState(0)
  const [newSchoolYears, setNewSchoolYears] = useState([]);
  const [valueDis, setValueDis] = useState([]);
  const [valueDisRPD, setValueDisRPD] = useState([]);
  const [numberPage, setNumberPage] = useState(0);
  const [firstRender, setFirstRender] = useState(true);
  const [type, setType] = useState('disciplines');

  // переход по страницам возвращает к началу таблицы, а не к началу документа
  const tableRef = useRef(null);

  // Списки-источники зависимых фильтров на момент монтирования.
  // Пока ссылка не поменялась, список ещё не загружен с сервера и сбрасывать выбор нельзя
  const initialCurriculumRef = useRef(curriculum);
  const initialDisciplinesRef = useRef(disciplines);

  const resetForm = () => {
    setCurriculumId(0)
    setDisciplineId(0)
    setDisciplineRPDId(0)
    setEyId(0)
    setSyid(0)
    setSchoolYearId(0)
    setNewSchoolYears([])
    setValueDis([])
    setValueDisRPD([])
    fetchYears()
    setNumberPage(0)
    fetchEducationPrograms(0, 0, 0, 0, type)
    props.location.state = {}
  }


  useEffect(() => {
    // «Все» в перечни не добавляем — эту опцию рисует сам CustomSelect
    const value = schoolYears ? schoolYears.map(el => ({ value: el.syid, name: el.name })).reverse() : []
    const valueDis = discipline?.map(el => ({ value: el.idmetacourse, name: el.name })) || []
    const valueDisRPD = disciplines?.map(el => ({ value: el.value, name: el.name })) || []

    setNewSchoolYears(value);
    setValueDis(valueDis)
    setValueDisRPD(valueDisRPD)
  }, [schoolYears, disciplines, disciplineRPD]);

  useEffect(() => {
    fetchYears()
    fetchDisciplines({ idcurriculum: 0, syid: 0, type })
    setInitialState()
  }, [])

  useEffect(() => {
    fetchCurriculum(syid)
  }, [syid])

  useEffect(() => {
    if (firstRender && props.location.state) return setFirstRender(false);

    fetchEducationPrograms(numberPage, curriculumId, disciplineRPDId, syid, type)
  }, [curriculumId, disciplineRPDId, numberPage, syid, type]);

  //TODO
  const curriculumOptions = (curriculum || []).map(el => ({ value: el.value, name: el.label }))

  useEffect(() => {
    if (curriculumId) {
      fetchMetaCourse(syid, curriculumId, 0);
    }
    fetchDisciplines({ idcurriculum: curriculumId, syid: syid, type });
  }, [curriculumId, numberPage, syid, type])

  useEffect(() => {
    fetchMetaCourse(syid, curriculumId, disciplineRPDId)
  }, [disciplineRPDId])

  // Год набора -> Учебный план: если выбранного плана нет в новом списке, ставим "Все"
  useEffect(() => {
    if (curriculum === initialCurriculumRef.current) return;
    if (curriculumId && !(curriculum || []).some(el => el.value == curriculumId)) {
      setCurriculumId(0);
      setNumberPage(0);
    }
  }, [curriculum, curriculumId]);

  // Год набора / Учебный план -> Дисциплина РПД: если дисциплины нет в новом списке, ставим "Все"
  useEffect(() => {
    if (disciplines === initialDisciplinesRef.current) return;
    if (disciplineRPDId && !(disciplines || []).some(el => el.value == disciplineRPDId)) {
      setDisciplineRPDId(0);
      setNumberPage(0);
    }
  }, [disciplines, disciplineRPDId]);


  const setInitialState = () => {
    const { schoolYearId, curriculumId, disciplineRPDId, disciplineId, numberPage } = props.location.state || {}
    setSchoolYearId(schoolYearId || 0)
    setSyid(schoolYearId || 0)
    setCurriculumId(curriculumId || 0)
    setDisciplineRPDId(disciplineRPDId || 0)
    setDisciplineId(disciplineId || 0)
    setNumberPage(numberPage || 0)
  }

  const changeType = (newType) => {
    if (newType === type) return;
    setDisciplineRPDId(0);
    setNumberPage(0);
    setType(newType);
  };

  const handlePageChange = (page) => {
    setNumberPage(page);
    if (tableRef.current) tableRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  const canOpenCard = type === 'disciplines';

  const goToCard = (plan) => () => {
    // Для практик и модулей открытие рабочей программы временно отключено
    if (!canOpenCard) return;

    const state = {
      schoolYearId,
      curriculumId,
      disciplineRPDId,
      disciplineId,
      numberPage,
      // сведения о программе карточка берёт из строки перечня: отдельного метода на них нет
      program: {
        spec: plan["Воен.специальн."],
        discipline: plan["Дисциплина"],
        name: plan["Название"],
        status: plan["Статус"],
        curriculumName: plan["Учебный план"],
      },
    }

    // Используем syid из данных plan если есть, иначе из фильтра, иначе 0
    const syidForRoute = plan.syid || schoolYearId || 0;
    props.history.push({ pathname: `/discipline/${plan.cid}/${plan.idcurriculum}/${syidForRoute}/0`, state })
  };

  const renderValue = (value) => (value || value === 0)
    ? value
    : <span className={styles.dash}>—</span>;

  const renderEducationProgramsTable = (plan, index) => (
    <tr key={index} onClick={goToCard(plan)}>
      <td className={styles.left}>{renderValue(plan["Воен.специальн."])}</td>
      <td className={styles.strong}>{renderValue(plan[type === 'modules' ? 'Модуль' : 'Дисциплина'])}</td>
      <td>{renderValue(plan["Название"])}</td>
      <td className={styles.colStatus}>{renderValue(plan["Статус"])}</td>
      <td className={styles.colPlan}>{renderValue(plan["Учебный план"])}</td>
      <td className={styles.colFiles}>
        {plan["Файлы"]
          ? <span className={styles.fileMark} title="Есть файлы"><FiFileText size={15} /></span>
          : <span className={styles.dash}>—</span>}
      </td>
    </tr>
  );

  const isFiltersDirty = !!(schoolYearId || curriculumId || disciplineRPDId);
  const rows = educationPrograms || [];
  const currentPage = numberPage + 1;

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <h1 className={styles.title}>Рабочие программы дисциплин (модулей)</h1>
        <div className={styles.headRight}>
          <button className={styles.resetBtn} onClick={resetForm} disabled={!isFiltersDirty}>
            Сбросить фильтры
            <FaRegTrashAlt size={16} />
          </button>
        </div>
      </div>

      <div className={styles.seg}>
        {TYPE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            className={type === opt.value ? styles.segOn : undefined}
            onClick={() => changeType(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className={styles.filters}>
        <div className={styles.fYear}>
          <CustomSelect
            title="Год набора"
            width="100%"
            options={newSchoolYears}
            value={schoolYearId || ALL}
            valuesName="syid"
            setFieldValue={(name, value) => {
              const next = toFilter(value);
              if (next == schoolYearId) return;
              setSyid(next)
              setSchoolYearId(next)
              setNumberPage(0)
            }}
          />
        </div>

        <div className={styles.fPlan}>
          <CustomSelect
            title="Учебный план"
            width="100%"
            options={curriculumOptions}
            value={curriculumId || ALL}
            valuesName="idcurriculum"
            setFieldValue={(name, value) => {
              const next = toFilter(value);
              if (next == curriculumId) return;
              setCurriculumId(next)
              setNumberPage(0)
            }}
          />
        </div>

        <div className={styles.fDisc}>
          <CustomSelect
            title={DISCIPLINE_FILTER_LABEL[type]}
            width="100%"
            options={valueDisRPD}
            value={disciplineRPDId || ALL}
            valuesName="cid"
            setFieldValue={(name, value) => {
              const next = toFilter(value);
              if (next == disciplineRPDId) return;
              setDisciplineRPDId(next)
              setNumberPage(0)
            }}
          />
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loader}>
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>Ничего не найдено</div>
          Измените условия поиска или сбросьте фильтры
        </div>
      ) : (
        <>
          <div className={styles.tableWrap} ref={tableRef}>
            <table className={canOpenCard ? `${styles.table} ${styles.clickRows}` : styles.table}>
              <thead>
                <tr>
                  <th>Воен.специальн.</th>
                  <th>{type === 'modules' ? 'Модуль' : 'Дисциплина'}</th>
                  <th>Название</th>
                  <th className={styles.colStatus}>Статус</th>
                  <th className={styles.colPlan}>Учебный план</th>
                  <th className={styles.colFiles}>Файлы</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(renderEducationProgramsTable)}
              </tbody>
            </table>
          </div>

          <div className={styles.footrow}>
            <span className={styles.total}>
              Найдено записей: <b>{totalCount || rows.length}</b>
            </span>
            <div className={styles.pager}>
              <button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(numberPage - 1)}
                aria-label="Предыдущая страница"
              >
                &#8249;
              </button>
              {pagerItems(currentPage, pagesCount).map((item, ind) => item === 'gap'
                ? <span key={`gap-${ind}`} className={styles.gap}>…</span>
                : <button
                    key={item}
                    className={item === currentPage ? styles.pageOn : undefined}
                    onClick={() => handlePageChange(item - 1)}
                  >
                    {item}
                  </button>
              )}
              <button
                disabled={currentPage >= pagesCount}
                onClick={() => handlePageChange(numberPage + 1)}
                aria-label="Следующая страница"
              >
                &#8250;
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EducationWorkPrograms;
